import { Fragment, ReactElement, SyntheticEvent, useEffect, useState } from "react";
import { useFloatingPopover } from "./floatingHelpers";
import { Popover as HuiPopover } from "@headlessui/react";
import { FloatingOverlay } from "./FloatingOverlay";
import { classNames } from "../utils/classNames";
import { isEscape } from "../utils/keyboardEvents";
import { stopPropagation } from "../utils/events";

export type PopoverProps = {
  button: (params: {
    toggleOpen: (event: SyntheticEvent) => void;
    setReference: (el: HTMLElement | null) => void;
  }) => ReactElement;
  content: ((params: { close: () => void }) => ReactElement) | ReactElement;
  bg?: "dark";
};

export function Popover(props: PopoverProps) {
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [popoverEl, setPopoverEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  const { floatingStyles } = useFloatingPopover({
    open,
    targetEl,
    popoverEl,
    autoUpdate: true,
    placement: "bottom",
  });

  const toggleOpen = stopPropagation(() => setOpen((open) => !open));

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (isEscape(event)) {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", listener, true);
    return () => {
      window.removeEventListener("keydown", listener, true);
    };
  }, []);

  return (
    <HuiPopover as={Fragment}>
      {() => (
        <>
          <HuiPopover.Button as={Fragment}>
            {props.button({ toggleOpen, setReference: setTargetEl })}
          </HuiPopover.Button>
          {open && (
            <FloatingOverlay layer={"popover"} modal onClick={toggleOpen}>
              <HuiPopover.Panel
                ref={setPopoverEl}
                className={classNames(
                  props.bg === "dark"
                    ? "bg-gray-x-hard color-primary-contrast"
                    : "bg-default color-default tw-border border-gray-soft",
                  "tw-rounded",
                  "tw-shadow-lg",
                  "tw-p-4",
                )}
                style={floatingStyles}
                onClick={(event) => {
                  // Prevent closing of the panel when clicked inside
                  event.stopPropagation();
                }}
                static
              >
                {typeof props.content === "function"
                  ? props.content({ close: () => setOpen(false) })
                  : props.content}
              </HuiPopover.Panel>
            </FloatingOverlay>
          )}
        </>
      )}
    </HuiPopover>
  );
}
